import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organization: any;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setAuth: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      setAuth: (user, token) => {
        localStorage.setItem('token', token);
        set({ user, token, isAuthenticated: true, isLoading: false });
      },
      logout: () => {
        localStorage.removeItem('token');
        set({ user: null, token: null, isAuthenticated: false, isLoading: false });
      },
      setLoading: (loading) => set({ isLoading: loading })
    }),
    {
      name: 'auth-storage'
    }
  )
);

interface InspectionState {
  currentInspection: any | null;
  offlineInspections: any[];
  setCurrentInspection: (inspection: any) => void;
  addOfflineInspection: (inspection: any) => void;
  updateOfflineInspection: (id: string, data: any) => void;
  removeOfflineInspection: (id: string) => void;
  clearOfflineInspections: () => void;
}

export const useInspectionStore = create<InspectionState>()(
  persist(
    (set, get) => ({
      currentInspection: null,
      offlineInspections: [],
      setCurrentInspection: (inspection) => set({ currentInspection: inspection }),
      addOfflineInspection: (inspection) => {
        const { offlineInspections } = get();
        set({ offlineInspections: [...offlineInspections, inspection] });
      },
      updateOfflineInspection: (id, data) => {
        const { offlineInspections } = get();
        set({
          offlineInspections: offlineInspections.map(i =>
            i.offlineId === id ? { ...i, ...data } : i
          )
        });
      },
      removeOfflineInspection: (id) => {
        const { offlineInspections } = get();
        set({
          offlineInspections: offlineInspections.filter(i => i.offlineId !== id)
        });
      },
      clearOfflineInspections: () => set({ offlineInspections: [] })
    }),
    {
      name: 'inspection-storage'
    }
  )
);

interface UIState {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

export const useUIStore = create<UIState>()((set) => ({
  sidebarOpen: true,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open })
}));